<script setup>
import { Dialog, DialogPanel } from "@headlessui/vue";
import CloseIcon from "@/components/svg-icons/CloseIcon.vue";
import WarningIcon from "@/components/shared/icons/WarningIcon.vue";
import Pagination from "@/components/shared/Pagination.vue";
import CheckboxSelect from "@/components/shared/select/CheckboxSelect.vue";
import VSelectCaret from "@/components/shared/select/VSelectCaret.vue";
import Magnifier from "@/components/shared/icons/Magnifier";
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import Spinner from "@/components/helpers/Spinner.vue";
import SearchBar from "@/components/shared/SearchBar.vue";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useVueTable,
  FlexRender,
} from "@tanstack/vue-table";
import { cloneDeep, debounce, unionBy, difference } from "lodash";
import {
  computed,
  defineEmits,
  reactive,
  unref,
  watch,
  toRefs,
  ref,
  toValue,
} from "vue";
import BadgeBar from "@/components/program-manager/sessions/components/BadgeBar";
import {
  convertHousingFilterCriteria,
  convertSTDFilters,
  getCityCountryOptionLabel,
} from "@/components/program-manager/sessions/utils";
import { columnsConfig } from "@/components/program-manager/sessions/components/Housing/constants";
import { PRICED_SECTIONS_EXCLUDE_FILTERS } from "@/components/program-manager/sessions/constants";
import {
  useGetCityOptions,
  useGetHousings,
} from "@/components/program-manager/sessions/composable";
import { useStore } from "vuex";
import { watchDebounced } from "@vueuse/core";
import {
  HOUSING_TYPES,
  TIERS_OBJ,
  ALLOWED_PROGRAM_TYPES_OPTIONS,
  SESSION_TYPES_NAMES,
} from "@/constants";

const props = defineProps({
  terms: {
    type: Array,
    default: () => [],
  },
  sessionTravelDetails: {
    type: Array,
    default: () => [],
  },
  sessionTypes: {
    type: Array,
    default: () => [],
  },
  sessionHostInstitutions: {
    type: Array,
    default: () => [],
  },
  modalIsOpen: {
    type: Boolean,
    default: false,
  },
  selectedHousing: {
    type: Array,
    default: () => [],
  },
  housingOwnerId: {
    type: String,
    required: false,
  },
});

const {
  sessionTravelDetails,
  sessionTypes,
  sessionHostInstitutions,
  terms,
  modalIsOpen,
  selectedHousing,
} = toRefs(props);

const warningModalIsOpen = ref(false);

const emit = defineEmits(["closeModal", "updateSelectedHousing"]);

const allowedProgramTypesValues = computed(() => {
  const sessionTypesOptions = sessionTypes.value?.map((type) => type?.name);
  return ALLOWED_PROGRAM_TYPES_OPTIONS.filter((item) => {
    if (
      (item?.label === SESSION_TYPES_NAMES.custom &&
        sessionTypesOptions.includes(SESSION_TYPES_NAMES.faculty_led)) ||
      (item?.label === SESSION_TYPES_NAMES.internship &&
        sessionTypesOptions.includes(SESSION_TYPES_NAMES.internship)) ||
      (item?.label === SESSION_TYPES_NAMES.study_abroad &&
        sessionTypesOptions.includes(SESSION_TYPES_NAMES.study))
    ) {
      return item;
    }
  });
});

const { newCities, newDates } = convertSTDFilters(unref(sessionTravelDetails));
const state = reactive({
  filters: {
    text: undefined,
    allowed_program_types: cloneDeep(unref(allowedProgramTypesValues)),
    terms: cloneDeep(unref(terms)),
    cities: newCities,
    date: newDates && newDates.length ? newDates[0] : undefined,
    pricing_date: sessionTravelDetails.value?.[0]?.arrival_date,
  },
  selectedHousings: selectedHousing.value
    ? cloneDeep(unref(selectedHousing))
    : [],
  dates: newDates,
  loadingAll: false,
});

const housingIndex = (housingId) => {
  return state.selectedHousings?.findIndex(({ id }) => id === housingId);
};

const housingIsSelected = (id) => {
  return housingIndex(id) >= 0;
};

const checkHousing = (housing) => {
  if (!housingIsSelected(housing.id)) {
    state.selectedHousings?.push(housing);
  } else {
    const index = housingIndex(housing.id);
    state.selectedHousings?.splice(index, 1);
  }
};

const filterLabelMap = {
  cities: ({ city_ascii = "" }) => city_ascii,
  types: ({ type = "" }) => type,
};

const pagination = reactive({
  pageIndex: 1,
  pageSize: 10,
});

const onCancel = () => {
  emit("closeModal");
};

const onCloseWarningModal = () => {
  warningModalIsOpen.value = false;
};

const addSelected = () => {
  if (sessionHostInstitutions?.value?.length) {
    const sessionHostInstitutionsIds =
      (
        sessionHostInstitutions?.value?.map(
          (institution) => institution.entity?.id
        ) ?? []
      )?.filter((item) => item) ?? [];
    const housingHostInstitutionsIds =
      state?.selectedHousings?.flatMap((housing) =>
        housing.allowed_participants.map(
          (institution) => institution.home_institution_id
        )
      ) ?? [];
    if (
      difference(sessionHostInstitutionsIds, housingHostInstitutionsIds)
        ?.length &&
      state.selectedHousings?.length
    ) {
      warningModalIsOpen.value = true;
    }
  }

  emit("updateSelectedHousing", state.selectedHousings);
};

const {
  execute: executeFetchCityOptions,
  isLoading: cityLoading,
  state: cityOptions,
  isReady: cityIsReady,
} = useGetCityOptions(
  { immediate: true, throwError: true },
  { city_ascii: "chi" },
  true
);

const {
  execute: fetchHousingOptions,
  isLoading: housingLoading,
  state: housingOptions,
  isReady: housingIsReady,
} = useGetHousings(
  { immediate: true, throwError: false },
  {
    limit: 20,
    extraParams: {
      ...convertHousingFilterCriteria(state.filters),
      owner_entity_id: toValue(props.housingOwnerId),
      all_available_housing: true,
      is_approved_for_subdomain: true,
    },
  },
  true
);

const executeFetchHousings = debounce(async (search) => {
  await fetchHousingOptions(0, {
    ...{ q: search },
    extraParams: {
      ...convertHousingFilterCriteria(state.filters),
      owner_entity_id: toValue(props.housingOwnerId),
      all_available_housing: true,
      is_approved_for_subdomain: true,
    },
    limit: pagination.pageSize,
    page: pagination.pageIndex,
    cancel: true,
  });
}, 500);

const fetchCityOptions = debounce(async (search, loading) => {
  if (search && search?.length > 2) {
    loading(true);
    try {
      await executeFetchCityOptions(0, {
        city_ascii: search,
        cancel: true,
      });
      loading(false);
    } catch (e) {
      if (e?.message !== "cancel") {
        loading(false);
      }
    }
  }
}, 0);

const store = useStore();
const getTermsData = computed(
  () => store.getters["programManager/getTermsData"]
);

const changePage = async (pageIndex) => {
  pagination.pageIndex = pageIndex;
  await executeFetchHousings(state.filters?.text);
};

const handleSearch = (search) => {
  state.filters.text = search;
  if (search && search?.length > 2) {
    executeFetchHousings(search);
  } else if (!search && search?.length) {
    executeFetchHousings();
  }
};

const resetFilters = () => {
  state.filters.text = undefined;
  state.filters.allowed_program_types = [];
  state.filters.terms = terms.value ? cloneDeep(unref(terms)) : [];
  state.filters.types = [];
  state.filters.cities = [];
  state.filters.date = undefined;
  state.dates = [];
};

watch(
  () => props.sessionTravelDetails,
  (newTravelDetails) => {
    const { newCities, newDates } = convertSTDFilters(newTravelDetails);
    state.filters.date = newDates && newDates.length ? newDates[0] : undefined;
    state.filters.cities = newCities || [];
    state.dates = newDates;
  },
  { deep: true }
);

watch(
  () => props.terms,
  (newTerms) => {
    state.filters.terms = cloneDeep(newTerms);
  },
  { deep: true }
);

watch(
  () => props.selectedHousing,
  (selectedHousing) => {
    state.selectedHousings = cloneDeep(selectedHousing) || [];
  },
  { deep: true }
);

watch(
  () => props.sessionTypes,
  () => {
    state.filters.allowed_program_types = cloneDeep(
      unref(allowedProgramTypesValues)
    );
  },
  { deep: true }
);

watchDebounced(
  [() => state.filters, () => props.housingOwnerId],
  async ([newFilters, _]) => {
    // Skipping new/old comparison since the new/old values are equal when doing deep watch per Vue.js docs.
    // https://vuejs.org/guide/essentials/watchers#deep-watchers
    // This seems like a small edge case since it's also debounced.
    const searchFilter = newFilters?.text;
    await executeFetchHousings(searchFilter);
  },
  { debounce: 2000, deep: true }
);

const columnHelper = createColumnHelper();
const columns = columnsConfig.map(
  ({ header, id, size, getColumnDisplayValue, type }) => {
    return columnHelper.accessor(getColumnDisplayValue, {
      id,
      header,
      size,
      type,
    });
  }
);
columns.map(() => columnHelper);
const table = useVueTable({
  get data() {
    const apiOptions = unref(housingOptions)?.data || [];
    const unionedHousing = unionBy(
      selectedHousing.value || [],
      apiOptions,
      "id"
    );
    return unionedHousing.map((housing) => {
      const tier = TIERS_OBJ?.[housing?.tier?.name] ?? "";
      return {
        ...housing,
        tier: {
          ...housing.tier,
          name: tier,
        },
      };
    });
  },
  columns: columns,
  columnResizeMode: "onChange",
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  manualPagination: true,
});
</script>

<template>
  <div>
    <Dialog
      :open="warningModalIsOpen"
      class="relative max-w-4xl"
      @close-modal="() => onCloseWarningModal()"
      @click.self="() => onCloseWarningModal()"
    >
      <div class="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel>
          <div
            class="fixed inset-0 z-0 hidden bg-university-primary opacity-50 sm:block"
          ></div>
          <div class="bg-white p-6 rounded-xl z-20 relative w-fit h-fit">
            <div class="text-right">
              <button @click="() => onCloseWarningModal()">
                <CloseIcon size="28" stroke="#007f80" stroke-width="1.75" />
              </button>
            </div>
            <section class="flex flex-col h-fit">
              <h1
                class="flex justify-center text-xl leading-tight text-center sm:text-2xl md:text-3xl mb-6 flex-0"
              >
                <WarningIcon class="mx-auto my-10" alt="Warning icon" />
              </h1>
              <div class="h-24">
                Selected Host Institution or Sponsoring Institution doesn't
                match Housing Institution.
              </div>
              <div class="flex align-center justify-center h-10 flex-0">
                <BaseButton
                  outlined
                  class="px-7"
                  :on-click="onCloseWarningModal"
                  >Close</BaseButton
                >
              </div>
            </section>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
    <Dialog
      :open="modalIsOpen"
      class="relative z-50"
      @close-modal="() => emit('closeModal')"
      @click.self="() => emit('closeModal')"
    >
      <div class="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel>
          <div
            class="fixed inset-0 z-0 hidden bg-university-primary opacity-50 sm:block"
          ></div>
          <div class="bg-white h-[95vh] p-6 rounded-xl z-20 relative w-[95vw]">
            <div class="text-right">
              <button @click="() => emit('closeModal')">
                <CloseIcon size="28" stroke="#007f80" stroke-width="1.75" />
              </button>
            </div>
            <section class="flex flex-col h-[85vh]">
              <h1
                class="flex justify-center text-xl leading-tight text-center sm:text-2xl md:text-3xl mb-6 flex-0"
              >
                Add Housing
              </h1>
              <SearchBar
                class="flex min-w-[300px] md:w-[400px] searchbar mb-2 ml-auto flex-0"
                @handle-search="handleSearch"
              >
                <template #advancedFilters><div></div></template>
              </SearchBar>
              <div class="flex bg-blue-450 rounded-md ml-2 flex-wrap flex-0">
                <CheckboxSelect
                  v-model="state.filters.types"
                  :clear-search-on-blur="() => false"
                  :close-on-select="false"
                  :components="{ OpenIndicator: Magnifier }"
                  :deselect-from-dropdown="true"
                  label="type"
                  :multiple="true"
                  option-key="type"
                  :options="HOUSING_TYPES"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  placeholder="Types"
                  scroll-input="auto"
                  input-max-height="29px"
                />
                <CheckboxSelect
                  v-model="state.filters.terms"
                  :clear-search-on-blur="() => false"
                  :close-on-select="false"
                  :components="{ OpenIndicator: Magnifier }"
                  :deselect-from-dropdown="true"
                  label="name"
                  :multiple="true"
                  :options="getTermsData"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  placeholder="Term"
                  scroll-input="auto"
                  input-max-height="29px"
                />
                <CheckboxSelect
                  v-model="state.filters.cities"
                  :clear-search-on-blur="() => false"
                  :close-on-select="false"
                  :components="{ OpenIndicator: Magnifier }"
                  :deselect-from-dropdown="true"
                  :filterable="false"
                  label="city_ascii"
                  :multiple="true"
                  :get-option-label="getCityCountryOptionLabel"
                  :options="cityOptions?.items || []"
                  :loading="cityLoading || !cityIsReady"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  placeholder="City"
                  scroll-input="auto"
                  input-max-height="29px"
                  @search="fetchCityOptions"
                >
                </CheckboxSelect>
                <CheckboxSelect
                  v-model="state.filters.allowed_program_types"
                  :options="ALLOWED_PROGRAM_TYPES_OPTIONS"
                  :clear-search-on-blur="() => false"
                  :close-on-select="false"
                  :deselect-from-dropdown="true"
                  :components="{ OpenIndicator: Magnifier }"
                  :multiple="true"
                  label="label"
                  option-key="label"
                  placeholder="Participants Allowed"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  scroll-input="auto"
                  input-max-height="29px"
                ></CheckboxSelect>
                <VSelectCaret
                  v-model="state.filters.date"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  :options="state.dates"
                  placeholder="Date"
                  label="label"
                  scroll-input="auto"
                  input-max-height="29px"
                >
                </VSelectCaret>
              </div>
              <div class="grid grid-cols-12 flex-0">
                <BadgeBar
                  v-model="state.filters"
                  :filter-label-map="filterLabelMap"
                  :exclude-filters="PRICED_SECTIONS_EXCLUDE_FILTERS"
                  class="col-span-11"
                />
                <div
                  class="ml-auto uppercase text-success-900 text-xs font-semibold mt-2 cursor-pointer"
                  @click="resetFilters"
                >
                  Reset Filters
                </div>
              </div>
              <div class="mt-4 mb-2 mr-auto pl-4 flex-0">
                {{ housingOptions?.count || "No" }} Housing Listings
              </div>
              <div class="flex-1 flex overflow-y-auto">
                <table v-show="!housingLoading && housingIsReady">
                  <thead class="bg-white sticky top-0">
                    <tr
                      v-for="headerGroup in table.getHeaderGroups()"
                      :key="headerGroup.id"
                    >
                      <th
                        v-for="header in headerGroup.headers"
                        :key="header.id"
                        :colSpan="header.colSpan"
                        :style="{ width: `${header.getSize()}px` }"
                        class="py-4 pr-3 pl-2 text-left text-sm items-center text-gray-900"
                      >
                        <div class="flex items-center">
                          <FlexRender
                            v-if="!header.isPlaceholder"
                            :render="header.column.columnDef.header"
                            :props="header.getContext()"
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white">
                    <tr
                      v-for="row in table.getRowModel().rows"
                      v-show="!housingLoading && housingIsReady"
                      :key="row.id"
                      class="hover:bg-blue-350"
                    >
                      <td
                        v-for="cell in row.getVisibleCells()"
                        :key="cell.column.id"
                        :style="{ width: `${cell.column.getSize()}px` }"
                        class="py-8 text-sm pl-2 first:text-teal-900 capitalize border-t-[1px] border-solid border-t-[#f1f2f4]"
                      >
                        <div v-if="cell.column.columnDef.type === 'anchor'">
                          <a
                            class="text-teal-900"
                            :href="`/program-manager/housing/${cell.row.original.id}`"
                            target="_blank"
                            >{{ cell.row.original.name }}</a
                          >
                        </div>
                        <div
                          v-else-if="cell.column.columnDef.type === 'checkbox'"
                        >
                          <input
                            type="checkbox"
                            class="h-4 w-4 border-gray-300 form-checkbox"
                            :checked="housingIsSelected(cell.row.original.id)"
                            @input="() => checkHousing(cell.row.original)"
                          />
                        </div>
                        <FlexRender
                          v-else
                          :render="cell.column.columnDef.cell"
                          :props="cell.getContext()"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <Spinner
                  v-show="housingLoading || !housingIsReady"
                  outer-classes="flex items-center justify-center bg-white h-full relative mx-auto"
                />
              </div>
              <Pagination
                class="mb-4 flex-0"
                :page="pagination.pageIndex"
                :total-of-records="housingOptions?.count || 0"
                :records-per-page="pagination.pageSize"
                @change-page="changePage"
              />
              <div class="flex align-center justify-center h-10 flex-0">
                <BaseButton
                  outlined
                  :on-click="onCancel"
                  :disabled="state.loadingAll"
                  class="px-7"
                  >Cancel</BaseButton
                >
                <BaseButton :on-click="addSelected" :disabled="state.loadingAll"
                  >Add Selected</BaseButton
                >
              </div>
            </section>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
.checkbox-placeholder :deep(.vs__search) {
  font-size: 14px;
}
</style>
